import LoginLayout from '@/layout/LoginLayout';
import { trpc } from '@/trpcClient';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@shelflix/ui-web/components/ui/radio-group';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { extractTenantSlug, getTenantDomain } from '@/utils/tenantDetection';

export default function LoginVerificationCodeValidate() {
  const verificationCodeValidateMutation = useMutation(
    trpc.authentication['verification-code-validate'].mutationOptions()
  );
  const verificationCodeReceiveMutation = useMutation(
    trpc.authentication['verification-code-receive'].mutationOptions()
  );
  const [searchParams] = useSearchParams();
  const isFirstAccess = searchParams.has('firstAccess');
  const hasCode = searchParams.has('hasCode');
  const title = isFirstAccess ? 'Primeiro acesso' : 'Recuperar senha';
  const successRedirectRoute = isFirstAccess ? '/define-password?firstAccess' : '/define-password';
  const [identifierType, setIdentifierType] = useState<'email' | 'registration'>('email');
  const [identifier, setIdentifier] = useState('');

  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      code: Array(6).fill(''),
    },
    mode: 'onSubmit',
  });

  const isCodeComplete = form.watch('code').every((digit) => digit !== '');

  const handleFocus = (e: React.FormEvent<HTMLInputElement>, index: number) => {
    if (e.currentTarget.value.length === 1 && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    const digits = pastedData.slice(0, 6).split('');

    // Fill the code array with pasted digits
    const newCode = Array(6).fill('');
    digits.forEach((digit, index) => {
      newCode[index] = digit;
      form.setValue(`code.${index}`, digit);
    });

    // Focus the next empty input or the last one if all filled
    const nextEmptyIndex = digits.length < 6 ? digits.length : 5;
    const nextInput = document.getElementById(`code-${nextEmptyIndex}`);
    nextInput?.focus();
  };

  const onSubmit = (data: { code: string[] }) => {
    if (verificationCodeReceiveMutation.isPending) return;

    // Se o usuário já possui o código, usar o identificador informado
    const emailToUse = hasCode ? identifier : localStorage.getItem('email');

    if (!emailToUse) {
      toast.error('Identificador não encontrado');
      return;
    }

    // Get tenant slug from current domain for multi-tenant isolation
    const tenantSlug = extractTenantSlug(getTenantDomain());

    const payload = {
      email: emailToUse,
      code: data.code.join(''),
      tenantSlug,
    };

    verificationCodeValidateMutation.mutate(payload, {
      onSuccess: async () => {
        localStorage.setItem('email', emailToUse);
        localStorage.setItem('code', payload.code);
        navigate(successRedirectRoute);
      },
      onError: (error) => {
        toast.error(`${error.message}`);
      },
    });
  };

  useEffect(() => {
    if (isCodeComplete) {
      form.handleSubmit(onSubmit)();
    }
  }, [isCodeComplete, form]);

  const resendCode = () => {
    if (verificationCodeReceiveMutation.isPending) return;

    // Se o usuário já possui o código, usar o identificador informado
    const emailToUse = hasCode ? identifier : localStorage.getItem('email');

    if (!emailToUse) {
      toast.error('Identificador não encontrado');
      return;
    }

    // Get tenant slug from current domain for multi-tenant isolation
    const tenantSlug = extractTenantSlug(getTenantDomain());

    const payload = {
      email: emailToUse,
      tenantSlug,
    };

    verificationCodeReceiveMutation.mutate(payload, {
      onSuccess: async () => {
        toast.success('Novo código de verificação enviado');
      },
      onError: (error) => {
        toast.error(`Erro inesperado ao reenviar código de verificação: ${error.message}`);
      },
    });
  };

  return (
    <LoginLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex flex-col gap-4">
          <div className="mb-4 text-center">
            <h2 className="text-lg font-bold text-[var(--primary)]">{title}</h2>
            <div className="mx-auto mt-4 max-w-xs text-sm text-gray-700">
              {hasCode
                ? 'Informe seu identificador e o código de 6 dígitos:'
                : 'Confira sua caixa de e-mail com o código de 6 dígitos, para preencher abaixo:'}
            </div>
          </div>

          {hasCode && (
            <>
              <div className="mb-4">
                <RadioGroup
                  value={identifierType}
                  onValueChange={(value) => setIdentifierType(value as 'email' | 'registration')}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="email" id="email" />
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">
                      E-mail
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="registration" id="registration" />
                    <label htmlFor="registration" className="text-sm font-medium text-gray-700">
                      Matrícula
                    </label>
                  </div>
                </RadioGroup>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700">
                  {identifierType === 'registration' ? 'Matrícula' : 'E-mail'}
                </label>
                <Input
                  type={identifierType === 'registration' ? 'text' : 'email'}
                  placeholder={identifierType === 'registration' ? 'Digite sua matrícula' : 'Digite seu e-mail'}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="mt-1"
                />
              </div>
            </>
          )}

          <div className="mb-4 flex justify-center gap-4">
            {form.getValues('code').map((_, index) => (
              <FormField
                key={index}
                control={form.control}
                name={`code.${index}`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      {/* TODO remover onInput depois de adicionar máscaras */}
                      <Input
                        type="text"
                        maxLength={1}
                        id={`code-${index}`}
                        placeholder=""
                        className="h-14 w-12 rounded-md border border-gray-300 text-center"
                        {...field}
                        autoComplete="off"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onPaste={index === 0 ? handlePaste : undefined}
                        onInput={(e) => {
                          const inputElement = e.target as HTMLInputElement;
                          inputElement.value = inputElement.value.replace(/[^0-9]/g, '');
                          form.setValue(`code.${index}`, inputElement.value);
                          handleFocus(e, index);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>

          <Button
            type="submit"
            loading={verificationCodeValidateMutation.isPending}
            className="w-full bg-[var(--primary)]"
            disabled={!isCodeComplete || verificationCodeValidateMutation.isPending || (hasCode && !identifier.trim())}
          >
            {verificationCodeValidateMutation.isPending ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Carregando...</span>
              </div>
            ) : (
              <>
                <Icon name="email" className="mr-2" />
                Validar código de segurança
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--secondary)]"
            onClick={resendCode}
            loading={verificationCodeReceiveMutation.isPending}
            disabled={verificationCodeReceiveMutation.isPending || (hasCode && !identifier.trim())}
          >
            {verificationCodeReceiveMutation.isPending ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Carregando...</span>
              </div>
            ) : (
              <>Enviar novamente</>
            )}
          </Button>

          {hasCode && (
            <Button
              type="button"
              variant="outline"
              className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--secondary)]"
              onClick={() => navigate('/verification-receive?firstAccess')}
            >
              Voltar
            </Button>
          )}
        </form>
      </Form>
    </LoginLayout>
  );
}
